import type React from 'react';

import { getLunaSampleUrl } from '@vlabs/api/axios/lp5-client';
import { useGetEvent } from '@vlabs/api/luna-api/client';
import type { TopMatchEvent } from '@vlabs/api/luna-api/model/TopMatchEvent';
import { DetectionIcon, InfoCircleIcon } from '@vlabs/icons';
import { SimilarityTag } from '@vlabs/shared/components/similarity-tag/SimilarityTag';
import { routes } from '@vlabs/shared/navigation/routes';
import { calcTagType } from '@vlabs/shared/utils';
import {
  FaceSample,
  BodySample,
  Tooltip,
  useWindowSize,
} from '@vlabs/uikit';
import { Link } from '@vlabs/uikit/components/controls/link/Link';
import { BaseSamplePlaceholder } from '@vlabs/uikit/components/sample/base-sample/BaseSamplePlaceholder';
import type { WithDataTestid } from '@vlabs/uikit/types';
import cn from 'classnames';
import { generatePath } from 'react-router-dom';

import { GoToEventDetailsLunaCircleAnimatedButton } from '@vlabs/monokit/features/go-to-event-details';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';
import { EmptyCard } from '@vlabs/monokit/shared/ui/empty-card';

import { useTopMatchEventCellTranslation } from './i18n';
import st from './TopMatchEventCell.module.sass';

interface TopMatchEventCellProps extends WithDataTestid {
  topMatch: TopMatchEvent;
}

const displayName = 'TopMatchEventCell';
const testid = buildTestid(displayName);

// FIXME Добавить права на просмотр сэмпла из GUI-3600 (http://git.visionlabs.ru/clementine/lerna/-/merge_requests/1228)
function TopMatchEventCell({
  topMatch,
  'data-testid': dataTestId = undefined,
}: TopMatchEventCellProps): React.JSX.Element {
  const { t } = useTopMatchEventCellTranslation();
  const { width } = useWindowSize();
  const smallWidth = width && width < 1920;

  const {
    event_id: eventId,
    external_id: externalId,
    similarity,
  } = topMatch;

  const { data: event } = useGetEvent(
    eventId ?? '',
    { target: ['face_detections', 'body_detections'] },
    { query: { enabled: !!eventId } },
  );

  if (!event) return (<EmptyCard className={cn(st.Empty, { [st.Empty_small]: !!smallWidth })} />);

  const kind = calcTagType(similarity);

  const faceSample = getLunaSampleUrl('faces', event?.face_detections?.[0]?.sample_id);
  const bodySample = getLunaSampleUrl('bodies', event?.body_detections?.[0]?.sample_id);

  const showFaceSample = !!faceSample || !bodySample;
  const showBodyButton = !!bodySample && !faceSample;

  const button = eventId ? (
    <GoToEventDetailsLunaCircleAnimatedButton
      eventId={eventId}
      size="small"
    />
  ) : undefined;

  return (
    <div
      className={cn(st.TopMatchEventCell, {
        [st.TopMatchEventCell_small]: !!smallWidth,
        [st[`TopMatchEventCell_${kind}`]]: !!kind,
      })}
      {...testid(dataTestId)}
    >
      <div className={st.Samples}>
        <FaceSample
          buttonOnTop={showFaceSample ? button : undefined}
          className={st.Sample}
          placeholder={(
            <BaseSamplePlaceholder
              placeholderType="image"
              size="large"
              tooltip={(
                <Tooltip tooltipContent={t('no_face')}>
                  <InfoCircleIcon />
                </Tooltip>
                )}
            />
            )}
          size={smallWidth ? 'medium' : 'large'}
          src={faceSample}
        />

        <BodySample
          buttonOnTop={showBodyButton ? button : undefined}
          className={st.Sample}
          placeholder={(
            <BaseSamplePlaceholder
              placeholderType="image"
              size="large"
              tooltip={(
                <Tooltip tooltipContent={t('no_body')}>
                  <InfoCircleIcon />
                </Tooltip>
                )}
            />
            )}
          size={smallWidth ? 'medium' : 'large'}
          src={bodySample}
        />
      </div>

      <div className={cn(st.Info, { [st.Info_small]: !!smallWidth })}>
        <SimilarityTag
          icon={<DetectionIcon />}
          size={smallWidth ? 'medium' : 'large'}
          title={t('tag')}
          value={similarity}
        />

        {!!externalId && (
          <div className={st.ExternalId}>
            <span className={cn(st.Value, { [st.Value_small]: !!smallWidth })}>
              {t('external_id')}
            </span>
            <Link
              size={smallWidth ? 'medium' : 'large'}
              to={generatePath(routes.externalId.read, { externalId })}
            >
              {externalId}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

TopMatchEventCell.displayName = displayName;

export { TopMatchEventCell };
